type SessionStoreConfig = {
  accessTokenKey: string;
  userKey: string;
  storage?: Storage;
};

type RecentFlagStoreConfig = {
  key: string;
  storage?: Storage;
  ttlMs?: number;
};

type SessionKeyStoreConfig = {
  key: string;
  storage?: Storage;
};

type SessionJsonStoreConfig = {
  key: string;
  storage?: Storage;
};

type SessionValueStoreConfig<TValue extends string> = {
  key: string;
  allowedValues: readonly TValue[];
  defaultValue?: TValue | null;
  storage?: Storage;
};

type SsoSessionStoreConfig<TAuthSource extends string = never> = {
  accessTokenKey: string;
  userKey: string;
  authSourceKey?: string;
  defaultAuthSource?: TAuthSource;
  storage?: Storage;
  clearAccessTokenExtras?: () => void;
  clearExtras?: () => void;
};

export type SessionStore<TUser> = {
  clearAccessToken: () => void;
  getAccessToken: () => string | null;
  getStoredUser: () => TUser | null;
  setSession: (accessToken: string, user: TUser) => void;
  setAccessToken: (accessToken: string) => void;
  setStoredUser: (user: TUser) => void;
  clearSession: () => void;
  isAuthenticated: () => boolean;
};

type SsoSessionStoreBase<TUser> = SessionStore<TUser>;

type SsoSessionStoreWithAuthSource<
  TUser,
  TAuthSource extends string,
> = SsoSessionStoreBase<TUser> & {
  clearStoredAuthSource: () => void;
  getStoredAuthSource: () => TAuthSource | null;
  setStoredAuthSource: (source: TAuthSource) => void;
};

type MemoryStorageStore = Map<string, string>;

function createMemoryStorage(backingStore: MemoryStorageStore): Storage {
  return {
    getItem: (key) => backingStore.get(key) ?? null,
    setItem: (key, value) => {
      backingStore.set(key, String(value));
    },
    removeItem: (key) => {
      backingStore.delete(key);
    },
    clear: () => {
      backingStore.clear();
    },
    key: (index) => Array.from(backingStore.keys())[index] ?? null,
    get length() {
      return backingStore.size;
    },
  };
}

const sessionMemoryStore: MemoryStorageStore = new Map();

function resolveOptionalStorage(storage?: Storage) {
  if (storage) {
    return storage;
  }
  if (typeof window === "undefined") {
    return null;
  }
  return window.sessionStorage;
}

function resolveSessionStorage(storage?: Storage) {
  if (storage) {
    return storage;
  }
  return createMemoryStorage(sessionMemoryStore);
}

function readTimestamp(storage: Storage | null, key: string) {
  const raw = storage?.getItem(key);
  const value = Number(raw || "");
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
}

export function createSessionStore<TUser>(config: SessionStoreConfig): SessionStore<TUser> {
  const getStorage = () => resolveSessionStorage(config.storage);

  const getAccessToken = () => {
    return getStorage()?.getItem(config.accessTokenKey) ?? null;
  };

  const getStoredUser = (): TUser | null => {
    const raw = getStorage()?.getItem(config.userKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as TUser;
    } catch {
      return null;
    }
  };

  const setSession = (accessToken: string, user: TUser) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(config.accessTokenKey, accessToken);
    storage.setItem(config.userKey, JSON.stringify(user));
  };

  const setAccessToken = (accessToken: string) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(config.accessTokenKey, accessToken);
  };

  const clearAccessToken = () => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(config.accessTokenKey);
  };

  const setStoredUser = (user: TUser) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(config.userKey, JSON.stringify(user));
  };

  const clearSession = () => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(config.accessTokenKey);
    storage.removeItem(config.userKey);
  };

  const isAuthenticated = () => {
    return Boolean(getAccessToken() && getStoredUser());
  };

  return {
    clearAccessToken,
    getAccessToken,
    getStoredUser,
    setSession,
    setAccessToken,
    setStoredUser,
    clearSession,
    isAuthenticated,
  };
}

export function createRecentFlagStore(config: RecentFlagStoreConfig) {
  const ttlMs = Math.max(1, config.ttlMs ?? 60 * 1000);
  const getStorage = () => resolveOptionalStorage(config.storage);

  const mark = (at = Date.now()) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(config.key, String(at));
  };

  const consume = (overrideTtlMs = ttlMs) => {
    const storage = getStorage();
    if (!storage) {
      return false;
    }
    const at = readTimestamp(storage, config.key);
    storage.removeItem(config.key);
    if (!at) {
      return false;
    }
    return Date.now() - at <= Math.max(1, overrideTtlMs);
  };

  const clear = () => {
    getStorage()?.removeItem(config.key);
  };

  const peek = () => {
    return readTimestamp(getStorage(), config.key);
  };

  return {
    clear,
    consume,
    mark,
    peek,
  };
}

export function createSessionKeyStore(config: SessionKeyStoreConfig) {
  const getStorage = () => resolveOptionalStorage(config.storage);

  const clear = () => {
    getStorage()?.removeItem(config.key);
  };

  return {
    clear,
  };
}

export function createSessionJsonStore<TValue>(config: SessionJsonStoreConfig) {
  const getStorage = () => resolveOptionalStorage(config.storage);

  const get = (): TValue | null => {
    const raw = getStorage()?.getItem(config.key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as TValue;
    } catch {
      return null;
    }
  };

  const set = (value: TValue | null | undefined) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    if (value == null) {
      storage.removeItem(config.key);
      return;
    }
    storage.setItem(config.key, JSON.stringify(value));
  };

  const clear = () => {
    getStorage()?.removeItem(config.key);
  };

  const consume = (): TValue | null => {
    const value = get();
    clear();
    return value;
  };

  return {
    clear,
    consume,
    get,
    set,
  };
}

export function createSessionValueStore<TValue extends string>(
  config: SessionValueStoreConfig<TValue>,
) {
  const getStorage = () => resolveOptionalStorage(config.storage);

  const get = (): TValue | null => {
    const raw = getStorage()?.getItem(config.key);
    if (raw !== null && config.allowedValues.includes(raw as TValue)) {
      return raw as TValue;
    }
    return config.defaultValue ?? null;
  };

  const set = (value: TValue | null | undefined) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    if (value == null) {
      storage.removeItem(config.key);
      return;
    }
    storage.setItem(config.key, value);
  };

  const clear = () => {
    getStorage()?.removeItem(config.key);
  };

  return {
    clear,
    get,
    set,
  };
}

export function createSsoSessionStore<TUser>(
  config: SsoSessionStoreConfig<never> & { authSourceKey?: undefined },
): SsoSessionStoreBase<TUser>;

export function createSsoSessionStore<TUser, TAuthSource extends string>(
  config: SsoSessionStoreConfig<TAuthSource> & {
    authSourceKey: string;
    defaultAuthSource?: TAuthSource | null;
  },
): SsoSessionStoreWithAuthSource<TUser, TAuthSource>;

export function createSsoSessionStore<TUser, TAuthSource extends string = never>(
  config: SsoSessionStoreConfig<TAuthSource>,
): SsoSessionStoreBase<TUser> | SsoSessionStoreWithAuthSource<TUser, TAuthSource> {
  const baseStore = createSessionStore<TUser>({
    accessTokenKey: config.accessTokenKey,
    userKey: config.userKey,
    ...(config.storage ? { storage: config.storage } : {}),
  });
  const getStorage = () => resolveOptionalStorage(config.storage);

  const clearAccessToken = () => {
    baseStore.clearAccessToken();
    config.clearAccessTokenExtras?.();
  };

  const clearSession = () => {
    baseStore.clearSession();
    config.clearAccessTokenExtras?.();
    config.clearExtras?.();
  };

  const api: SsoSessionStoreBase<TUser> = {
    ...baseStore,
    clearAccessToken,
    clearSession,
  };

  if (!config.authSourceKey) {
    return api;
  }

  const authSourceKey = config.authSourceKey;

  const getStoredAuthSource = (): TAuthSource | null => {
    const raw = getStorage()?.getItem(authSourceKey);
    if (raw) {
      return raw as TAuthSource;
    }
    return config.defaultAuthSource ?? null;
  };

  const setStoredAuthSource = (source: TAuthSource) => {
    const storage = getStorage();
    if (!storage) {
      return;
    }
    storage.setItem(authSourceKey, source);
  };

  const clearStoredAuthSource = () => {
    getStorage()?.removeItem(authSourceKey);
  };

  return {
    ...api,
    clearStoredAuthSource,
    getStoredAuthSource,
    setStoredAuthSource,
  };
}
